import React from 'react';
import { JcErrorView } from './JcErrorView';
import { reportError } from '../../data/JcErrorReporter';

export interface JcErrorBoundaryProps {
    readonly children: React.ReactNode;
    /** Render a custom fallback instead of the default {@link JcErrorView}. `retry` clears the caught error. */
    readonly fallback?: (error: Error, retry: () => void) => React.ReactNode;
    /** Notified on every caught error — a hook for external logging (e.g. Sentry). */
    readonly onError?: (error: Error, info: React.ErrorInfo) => void;
    /** Called after a soft retry clears the error, before re-rendering children. */
    readonly onReset?: () => void;
    /** When this value changes while an error is showing, the error is cleared (e.g. on navigation). */
    readonly resetKey?: unknown;
}

interface JcErrorBoundaryState {
    readonly error: Error | null;
}

/**
 * Catches any error thrown while rendering its subtree and shows a recovery
 * screen instead of a blank or stuck page. Pair with {@link JcAsyncErrorBoundary}
 * to also catch async failures (e.g. snapshot listener errors).
 */
export class JcErrorBoundary extends React.Component<JcErrorBoundaryProps, JcErrorBoundaryState> {
    constructor(props: JcErrorBoundaryProps) {
        super(props);
        this.state = { error: null };
        this.reset = this.reset.bind(this);
    }

    static getDerivedStateFromError(error: Error): JcErrorBoundaryState {
        return { error };
    }

    componentDidCatch(error: Error, info: React.ErrorInfo): void {
        reportError(error, { componentStack: info.componentStack });
        this.props.onError?.(error, info);
    }

    componentDidUpdate(prevProps: JcErrorBoundaryProps): void {
        // Navigating within a still-mounted boundary clears a caught error.
        if (this.state.error && this.props.resetKey !== prevProps.resetKey)
            this.setState({ error: null });
    }

    /** Soft recovery: clears the error so children re-render without a hard reload. */
    private reset(): void {
        this.setState({ error: null });
        this.props.onReset?.();
    }

    render(): React.ReactNode {
        const { error } = this.state;
        if (error) {
            return this.props.fallback
                ? this.props.fallback(error, this.reset)
                : <JcErrorView onRetry={this.reset} />;
        }
        return this.props.children;
    }
}
